import { createUUIDv4 } from '../util/helpers';
export const ApckTabs = {
    props: {
        // if this is set store the tab-id inside localStorage indexed by `localStorageKey`
        localStorageKey: {
            type: String,
            default: null,
        },
        // whether to store the last visited tab in the URL query
        updateURLQuery: {
            type: Boolean,
            default: true,
        },
        vertical: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            active: 0,
            tabs: [],
        };
    },
    methods: {
        registerTab(tab) {
            // Ensure this gets called from each tab to add it to the tabs array
            this.tabs.push(tab);
        },
        selectTab: function(index) {
            if(this.tabs[index] && !this.tabs[index].isActive) {
                this.tabs.forEach(tab => {
                    tab.isActive = false;
                });
                this.tabs[index].isActive = true;
                // if we have a key to index local storage by
                // we want to keep the currently active tab in
                // there, and not in the query string.
                if (this.localStorageKey) {
                    localStorage.setItem(this.localStorageKey, this.tabs[index].title);
                }
                // we don't have a key: update query string
                else if (this.updateURLQuery) {
                    const query = this.$route?.query || {};
                    this.$router.push({
                        query: {
                            // this is to preserve the existing query parameters
                            ...query,
                            tab: this.tabs[index].title
                        }
                    }).catch(()=>{});
                }
                this.$emit("selectTab", index);
            }
        },
        updateTabClass(index, cls) {
            if (index < 0 || index >= this.tabs.length) return '';
            const tab = this.tabs[index];
            tab.class = cls;
            return tab.class;
        },
        getBadgeClass(index) {
            return `nav-badge--${this.tabs[index].badge}`;
        },
        hasIcon(index) {
            const icon = this.tabs[index]?.icon;
            return Array.isArray(icon) ? icon.length > 0 : !!icon;
        },
        getIconClasses(index) {
            const icon = this.tabs[index]?.icon;
            if (!icon) {
                return [];
            }
            if (Array.isArray(icon)) {
                return ['fa', ...icon];
            }
            return ['fa', icon];
        }
    },
    watch: {
        tabs() {
            var tab = 0;
            if (this.localStorageKey) {
                const tabName = localStorage.getItem(this.localStorageKey) || '';
                const tabIndex = this.tabs.findIndex((t) => t.title.toLowerCase() === tabName.toLowerCase());
                this.selectTab(tabIndex);
            } else if (this.$route.query) {
                var _tab_name = this.$route.query.tab;
                if (_tab_name) {
                    this.tabs.forEach((tab, index) => {
                        if (tab.title.toLowerCase() === _tab_name.toLowerCase()) {
                            this.selectTab(index);
                        }
                    });
                }
            }
        },
    },
    template: `
        <div :class="['tabs', vertical ? 'tabs-vertical' : 'tabs-horizontal']">
            <div class="tabs-header d-flex justify-content-between">
               <nav>
                    <ul class="tab-list">
                        <li
                            v-bind:class="[tab.isActive ? 'active' : '', 'tab-nav']"
                            v-bind:id="tab.id"
                            v-for="(tab, index) in tabs"
                            v-on:click="selectTab(index)"
                            v-bind:data-showme="tab.dataShowme ? tab.dataShowme+'-tab' : null"
                        >
                            <a href="javascript:;" v-bind:class="[tab.class]">
                                {{ tab.title }}
                                <div v-if="tab.badge" :class="['nav-badge', getBadgeClass(index)]"></div>
                                <i v-if="hasIcon" :class="getIconClasses(index)"></i>
                            </a>
                        </li>
                    </ul>
                    <slot name="action"></slot>
               </nav>
                <slot name="navdiv"></slot>
            </div>
            <div class="tab-panes">
                <slot :updateTabClass="updateTabClass"></slot>
            </div>
        </div>
    `
}

export const ApckTab = {
    props: {
        // DOM ID to identify the component with
        id: {
            type: String,
            default: () => createUUIDv4(),
        },
        title: {
            type: String,
            required: true
        },
        active: {
            type: Boolean,
            default: false
        },
        dataShowme: {
            type: String,
            required: false,
            default: null,
        },
        badge: {
            type: String,
            default: '',
        },
        icon: {
            type: [String, Array]
        }
    },
    watch: {
        active(newValue) {
            this.isActive = newValue;
        },
    },
    template: `
        <div
            :id="id"
            v-show="isActive"
            v-bind:class="[isActive ? 'active' : '', 'tab-pane']"
            :data-showme="dataShowme"
        >
            <slot></slot>
        </div>
    `,
    data() {
        return {
            isActive: false,
            class: '',
        }
    },
    mounted() {
        this.isActive = this.active;

        // Register this tab with the parent ApckTabs
        if (this.$parent && typeof this.$parent.registerTab === 'function') {
            // We register the tabs from ApckTab rather than the parent
            // This is because previously an empty tab button was being created for any component that is a child of ApckTabs
            this.$parent.registerTab(this);
        }
    }
}

// The only difference between this and ApckTab is that this one uses `v-if` to defer the component
// load until one clicks into a particular tab.
export const ApckTabDefer = {
    extends: ApckTab,
    template: `
        <div
            :id="id"
            v-if="isActive"
            v-bind:class="[isActive ? 'active' : '', 'tab-pane']"
        >
            <slot></slot>
        </div>
    `,
};
